import { useContext } from "react";
import { LoadingContext } from "./loading.context";
import {
  getApiBaseUrl,
  getEsriPortalUrl,
  getEsriApiKey,
  getEsriPortalMapId,
  getEsriPortalSceneId
} from "./appSettings";

/**
 * Exported variable holding the current default API URL.
 *
 * IMPORTANT:
 * - We export it as `let` instead of `const` so we can update it
 *   AFTER loading configuration from `appsettings.json`.
 * - From the rest of the app perspective, you still import it as:
 *       import { DefaultURl } from "./defaultUrl";
 *   and use it like a normal constant (ex: `url={DefaultURl}`).
 */
export let DefaultURl = "https://localhost:7249/api/";

/**
 * Runtime ESRI / ArcGIS configuration variables.
 *
 * These follow the same pattern as DefaultURl:
 * - exported as `let`
 * - updated at runtime after loading appsettings.json
 */
export let EsriPortalUrl = "https://www.arcgis.com";
export let EsriApiKey = "";
export let EsriPortalMapId = "";
export let EsriPortalSceneId = "";

/**
 * Applies the runtime configuration to the DefaultURl variable.
 *
 * This should be called ONCE after `loadAppSettings()` has successfully
 * loaded the configuration from `appsettings.json`.
 *
 * The goal is:
 *  - Keep the same public API (`DefaultURl` export)
 *  - But update its value at runtime to match whatever is defined in config.
 */
export function applyConfigToDefaultUrl() {
  try {
    const apiBaseUrl = getApiBaseUrl();
    DefaultURl = apiBaseUrl;
  } catch (error) {
    // If configuration is not available for some reason,
    // we fall back to the original hardcoded value and log the error.
    console.error(
      "Failed to apply config to DefaultURl. Falling back to default value.",
      error
    );
  }
}

/**
 * Applies the runtime configuration to ESRI / ArcGIS settings.
 *
 * This should be called ONCE after `loadAppSettings()` has successfully
 * loaded the configuration from `appsettings.json`.
 *
 * The goal is identical to DefaultURl:
 *  - Keep the same public API (exported variables)
 *  - But update their values at runtime from configuration.
 */
export function applyConfigToMapSettings() {
  try {
    EsriPortalUrl = getEsriPortalUrl() || EsriPortalUrl;
    EsriApiKey = getEsriApiKey() || EsriApiKey;
    EsriPortalMapId = getEsriPortalMapId() || EsriPortalMapId;
    EsriPortalSceneId = getEsriPortalSceneId() || EsriPortalSceneId;
  } catch (error) {
    console.error(
      "Failed to apply config to ESRI map settings. Falling back to default values.",
      error
    );
  }
}

/**
 * Returns the effective URL to use for API calls.
 *
 * Priority:
 *  1) If the LoadingContext provides a `DEFAULTURL`, use that.
 *  2) Otherwise use the global `DefaultURl` value (which may have been
 *     updated by `applyConfigToDefaultUrl()` after loading appsettings.json).
 *
 * Signature is kept identical to your original implementation so that
 * existing code does not need to change.
 */
export function DefaultFunctionURL() {
  const ctx = useContext(LoadingContext);
  return ctx.DEFAULTURL ? ctx.DEFAULTURL : DefaultURl;
}
